import React, { useCallback } from "react";
import { LoadingIndicator, ErrorMessage } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { formatError, useReportError } from "#common/hooks/useReportError";
import { PaymentMethod } from "../../../../common/enums";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { useGetDetailedPaymentPref } from "../../hooks/use-get-detailed-payment-pref";
import { useSetBankCountry } from "../../hooks/use-set-bank-country";
import { getIdFromCountry } from "../../mappers";
import { PaymentPrefLayoutForm } from "../PaymentPrefLayoutForm";
import { type ChildComponentProps } from "../types";
import { AccountDetails } from "./AccountDetails";
import { BankDetails } from "./BankDetails";
import { WirePaymentPrefInputSchema, type WirePaymentPrefInputType } from "./constants";
import { mapContextStateToFormState, mapFormStateToContextState } from "./utils";

export const WirePaymentPrefInput: React.FC<ChildComponentProps> = ({
  onContinueClick,
  onBackClick,
  onBackButtonName = "Back",
}) => {
  const { setPaymentPrefDetail, workflowMode, selectedBankCountry, bankCountryLocked } =
    useConsolidatedPaymentPrefContext();
  const { data: paymentPrefDetail, loading, error } = useGetDetailedPaymentPref();
  const isInternationalPaymentRailsEnabled = useFeatureFlag("internationalPaymentRails", false);

  useReportError(error ? formatError(error) : null);

  const {
    loading: countryLoading,
    error: countryError,
    resolveAndSet,
  } = useSetBankCountry({ paymentPrefDetail });

  // Lock only when the country was set by an upstream source (country-picker or edit-hydration),
  // not when it was echoed back by this form's own submit (fixes FF-7684).
  const isBankCountryLocked = isInternationalPaymentRailsEnabled && bankCountryLocked;

  const handleFormSubmission = useCallback(
    (values: WirePaymentPrefInputType) => {
      // Push the latest bank-country selection into context before submitting so that
      // any consumer reading from context observes the most recent value.
      if (!selectedBankCountry && values.bankCountry) {
        resolveAndSet({ countryId: getIdFromCountry(values.bankCountry) });
      }
      setPaymentPrefDetail(mapFormStateToContextState(values));
      onContinueClick();
    },
    [setPaymentPrefDetail, onContinueClick, selectedBankCountry, resolveAndSet],
  );

  if (loading || countryLoading) {
    return <LoadingIndicator />;
  }

  if (error || countryError) {
    return <ErrorMessage>Failed to load payment preference</ErrorMessage>;
  }

  return (
    <PaymentPrefLayoutForm
      paymentMethod={PaymentMethod.Wire}
      onSubmit={handleFormSubmission}
      initialValues={mapContextStateToFormState(
        paymentPrefDetail?.__typename === "WirePaymentPrefObjectType"
          ? paymentPrefDetail
          : undefined,
        selectedBankCountry,
      )}
      validationSchema={WirePaymentPrefInputSchema}
      onBackClick={onBackClick}
      onBackButtonName={onBackButtonName}
      workflowMode={workflowMode}
    >
      <BankDetails isBankCountryLocked={isBankCountryLocked} />
      <AccountDetails />
    </PaymentPrefLayoutForm>
  );
};
